import { useState, useCallback } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { read, utils } from "xlsx";
import { useDropzone } from "react-dropzone";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { MaterialsByDefiner, updateMaterial } from "../materials/query";

const STATUS_CONFIG = {
  match: { label: "Match", color: "success", bgcolor: "#e8f5e9" },
  mismatch: { label: "Mismatch", color: "error", bgcolor: "#ffebee" },
  code_conflict_fixable: {
    label: "Code Conflict (Fixable)",
    color: "warning",
    bgcolor: "#fff3e0",
  },
  code_conflict_error: {
    label: "Code Conflict (Discrepancy)",
    color: "error",
    bgcolor: "#ffebee",
  },
  not_in_upload: {
    label: "Not in Upload",
    color: "default",
    bgcolor: "#f5f5f5",
  },
  not_in_db: {
    label: "Not in Material Table",
    color: "warning",
    bgcolor: "#fff8e1",
  },
};

function parseXlsx(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = read(e.target.result, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = utils.sheet_to_json(sheet, { defval: "" });
        const normalized = rows.map((row) => {
          const out = {};
          Object.keys(row).forEach((key) => {
            out[key.trim().toUpperCase()] = row[key];
          });
          return out;
        });
        resolve(normalized);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function mfrMpnKey(mfr, mpn) {
  return `${String(mfr || "").trim().toUpperCase()}||${String(mpn || "").trim().toUpperCase()}`;
}

function computeResults(dbMaterials, allDbMaterials, uploadedRows, entityID) {
  // Build upload lookup by MFR+MPN
  const uploadedByMfrMpn = {};
  uploadedRows.forEach((row) => {
    const key = mfrMpnKey(row.MFR, row.MPN);
    uploadedByMfrMpn[key] = {
      mfr: String(row.MFR || "").trim(),
      mpn: String(row.MPN || "").trim(),
      code: String(row.CODE || "").trim(),
      quantity: parseFloat(row.QUANTITY) || 0,
    };
  });

  // Attach the entity-specific code to each DB material.
  // Prefer the relation matching the current entityID; fall back to any available code.
  const materialsWithCode = dbMaterials.map((m) => {
    const rels = m.materialEntityRelations?.items || [];
    const rel = rels.find((r) => r.entityID === entityID) || rels.find((r) => r.code);
    return { ...m, entityCode: rel?.code || null };
  });

  // Group DB materials by their entity code
  const codeToDbMaterials = {};
  materialsWithCode.forEach((m) => {
    if (!m.entityCode) return;
    if (!codeToDbMaterials[m.entityCode]) codeToDbMaterials[m.entityCode] = [];
    codeToDbMaterials[m.entityCode].push(m);
  });

  // Group uploaded rows by code
  const codeToUploaded = {};
  uploadedRows.forEach((row) => {
    const code = String(row.CODE || "").trim();
    if (!code) return;
    if (!codeToUploaded[code]) codeToUploaded[code] = [];
    codeToUploaded[code].push({
      mfr: String(row.MFR || "").trim(),
      mpn: String(row.MPN || "").trim(),
      code,
      quantity: parseFloat(row.QUANTITY) || 0,
    });
  });

  const results = [];

  materialsWithCode.forEach((m) => {
    const key = mfrMpnKey(m.mfr, m.mpn);
    const uploadedMatch = uploadedByMfrMpn[key];
    const code = m.entityCode;
    const displayCode = code || (uploadedMatch ? String(uploadedMatch.code || "").trim() : "") || "—";

    if (!uploadedMatch) {
      results.push({
        materialId: m.id,
        mfr: m.mfr,
        mpn: m.mpn,
        code: displayCode,
        dbInventory: m.inventory,
        uploadedQuantity: null,
        status: "not_in_upload",
        canUpdate: false,
        updatedQuantity: null,
      });
      return;
    }

    const dbMatsForCode = code ? codeToDbMaterials[code] || [] : [];
    const uploadedForCode = code ? codeToUploaded[code] || [] : [];
    const isMultiCodeCase =
      dbMatsForCode.length > 1 || uploadedForCode.length > 1;

    if (isMultiCodeCase) {
      const totalDb = dbMatsForCode.reduce(
        (sum, x) => sum + (x.inventory || 0),
        0
      );
      const totalUploaded = uploadedForCode.reduce(
        (sum, x) => sum + (x.quantity || 0),
        0
      );
      const totalsMatch = Math.abs(totalDb - totalUploaded) < 0.0001;
      const needsUpdate =
        totalsMatch &&
        Math.abs((m.inventory || 0) - uploadedMatch.quantity) > 0.0001;

      results.push({
        materialId: m.id,
        mfr: m.mfr,
        mpn: m.mpn,
        code: displayCode,
        dbInventory: m.inventory,
        uploadedQuantity: uploadedMatch.quantity,
        status: totalsMatch ? "code_conflict_fixable" : "code_conflict_error",
        canUpdate: needsUpdate,
        updatedQuantity: totalsMatch ? uploadedMatch.quantity : null,
        totalDbInventory: totalDb,
        totalUploaded,
      });
    } else {
      const isMatch =
        Math.abs((m.inventory || 0) - uploadedMatch.quantity) < 0.0001;
      results.push({
        materialId: m.id,
        mfr: m.mfr,
        mpn: m.mpn,
        code: displayCode,
        dbInventory: m.inventory,
        uploadedQuantity: uploadedMatch.quantity,
        status: isMatch ? "match" : "mismatch",
        canUpdate: false,
        updatedQuantity: null,
      });
    }
  });

  // Keys already covered by the first loop (eligible = non-zero inventory)
  const handledKeys = new Set(
    materialsWithCode.map((m) => mfrMpnKey(m.mfr, m.mpn))
  );
  // All DB keys, including zero/null inventory, to distinguish "not in DB" from "in DB but zero"
  const allDbKeys = new Set(
    allDbMaterials.map((m) => mfrMpnKey(m.mfr, m.mpn))
  );
  uploadedRows.forEach((row) => {
    const key = mfrMpnKey(row.MFR, row.MPN);
    if (handledKeys.has(key)) return; // already shown in first loop
    const uploadQty = parseFloat(row.QUANTITY) || 0;
    if (!allDbKeys.has(key)) {
      results.push({
        materialId: key,
        mfr: String(row.MFR || "").trim(),
        mpn: String(row.MPN || "").trim(),
        code: String(row.CODE || "").trim() || "—",
        dbInventory: null,
        uploadedQuantity: uploadQty,
        status: "not_in_db",
        canUpdate: false,
        updatedQuantity: null,
      });
    } else {
      // Material exists in DB but has 0/null inventory — show as mismatch
      const dbMat = allDbMaterials.find((m) => mfrMpnKey(m.mfr, m.mpn) === key);
      results.push({
        materialId: dbMat?.id || key,
        mfr: String(row.MFR || "").trim(),
        mpn: String(row.MPN || "").trim(),
        code: String(row.CODE || "").trim() || "—",
        dbInventory: dbMat?.inventory ?? 0,
        uploadedQuantity: uploadQty,
        status: "mismatch",
        canUpdate: false,
        updatedQuantity: null,
      });
    }
  });

  // Sort: errors and conflicts first, matches last
  const order = {
    code_conflict_error: 0,
    not_in_db: 1,
    mismatch: 2,
    code_conflict_fixable: 3,
    not_in_upload: 4,
    match: 5,
  };
  results.sort((a, b) => (order[a.status] ?? 99) - (order[b.status] ?? 99));

  return results;
}

export default function InventoryCheck({ entityID, userRole }) {
  const client = useApolloClient();
  const [uploadedRows, setUploadedRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const isAdmin = userRole?.includes("admin");

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setFileName(file.name);
    setResults(null);
    setUpdateSuccess(false);
    setUpdateError(null);
    try {
      const rows = await parseXlsx(file);
      setUploadedRows(rows);
    } catch (err) {
      console.error("Error parsing XLSX:", err);
      setUpdateError(
        "Failed to parse file. Please check it has columns: MFR, MPN, CODE, QUANTITY."
      );
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
  });

  async function runCheck() {
    if (!uploadedRows || !entityID) return;
    setLoading(true);
    setUpdateSuccess(false);
    setUpdateError(null);
    try {
      let allMaterials = [];
      let nextToken = null;
      do {
        const { data } = await client.query({
          query: gql(MaterialsByDefiner),
          variables: { definerID: entityID, limit: 5000, nextToken },
          fetchPolicy: "network-only",
        });
        allMaterials = [
          ...allMaterials,
          ...(data?.MaterialsByDefiner?.items || []),
        ];
        nextToken = data?.MaterialsByDefiner?.nextToken;
      } while (nextToken);

      // Only compare materials with non-null, non-zero inventory
      const eligible = allMaterials.filter(
        (m) => m.inventory != null && m.inventory !== 0 && !m.isDeleted
      );

      const rowsWithQuantity = uploadedRows.filter(
        (row) => String(row.QUANTITY ?? "").trim() !== ""
      );
      setResults(computeResults(eligible, allMaterials, rowsWithQuantity, entityID));
    } catch (err) {
      console.error("Error running inventory check:", err);
      setUpdateError(
        "Error loading materials from database. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  async function applyFixes() {
    const toFix = results.filter(
      (r) => r.canUpdate && r.status === "code_conflict_fixable"
    );
    setUpdating(true);
    setConfirmOpen(false);
    setUpdateError(null);
    try {
      for (const item of toFix) {
        await client.mutate({
          mutation: gql(updateMaterial),
          variables: {
            input: { id: item.materialId, inventory: item.updatedQuantity },
          },
        });
      }
      setUpdateSuccess(true);
      await runCheck();
    } catch (err) {
      console.error("Error applying fixes:", err);
      setUpdateError(
        "An error occurred while updating. Some items may not have been saved."
      );
    } finally {
      setUpdating(false);
    }
  }

  const fixableItems =
    results?.filter(
      (r) => r.canUpdate && r.status === "code_conflict_fixable"
    ) || [];

  return (
    <Stack spacing={3} p={3}>
      <Typography variant="h4" fontWeight={300} color="secondary">
        Inventory Check
      </Typography>

      {/* Drop zone */}
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
          maxWidth: 600,
          transition: "border-color 0.2s",
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="body1" mt={1}>
          {isDragActive
            ? "Drop the file here..."
            : "Drag & drop an XLSX file here, or click to select"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Expected columns: MFR, MPN, CODE, QUANTITY
        </Typography>
        {fileName && (
          <Typography variant="body2" mt={1} color="primary.main">
            {fileName} — {uploadedRows?.length ?? 0} rows loaded
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        onClick={runCheck}
        disabled={!uploadedRows || loading}
        sx={{ alignSelf: "flex-start" }}
        startIcon={
          loading ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {loading ? "Checking..." : "Run Check"}
      </Button>

      {updateSuccess && (
        <Alert severity="success" onClose={() => setUpdateSuccess(false)}>
          Inventory updated successfully.
        </Alert>
      )}
      {updateError && (
        <Alert severity="error" onClose={() => setUpdateError(null)}>
          {updateError}
        </Alert>
      )}

      {results && (
        <>
          {/* Summary chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
              const count = results.filter((r) => r.status === status).length;
              if (!count) return null;
              return (
                <Chip
                  key={status}
                  label={`${cfg.label}: ${count}`}
                  color={cfg.color === "default" ? undefined : cfg.color}
                  variant="outlined"
                  size="small"
                />
              );
            })}
          </Stack>

          {/* Fix button — admin only, only when there are fixable conflicts */}
          {isAdmin && fixableItems.length > 0 && (
            <Box>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setConfirmOpen(true)}
                disabled={updating}
                startIcon={
                  updating ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {updating
                  ? "Updating..."
                  : `Fix ${fixableItems.length} Code Conflict(s)`}
              </Button>
            </Box>
          )}

          {/* Results table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>MFR</TableCell>
                  <TableCell>MPN</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">DB Inventory</TableCell>
                  <TableCell align="right">Uploaded Qty</TableCell>
                  <TableCell align="right">Difference</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => {
                  const cfg = STATUS_CONFIG[row.status];
                  const diff =
                    row.uploadedQuantity != null
                      ? (row.dbInventory || 0) - row.uploadedQuantity
                      : null;
                  return (
                    <TableRow
                      key={row.materialId}
                      sx={{ bgcolor: cfg.bgcolor }}
                    >
                      <TableCell>{row.mfr}</TableCell>
                      <TableCell>{row.mpn}</TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell align="right">{row.dbInventory}</TableCell>
                      <TableCell align="right">
                        {row.uploadedQuantity ?? "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            diff !== null && diff !== 0
                              ? "error.main"
                              : "inherit",
                          fontWeight:
                            diff !== null && diff !== 0 ? "bold" : "normal",
                        }}
                      >
                        {diff !== null ? diff : "—"}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip
                            label={cfg.label}
                            color={
                              cfg.color === "default" ? undefined : cfg.color
                            }
                            size="small"
                          />
                          {(row.status === "code_conflict_fixable" ||
                            row.status === "code_conflict_error") && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Code total — DB:{" "}
                              {row.totalDbInventory?.toFixed(2)}, Upload:{" "}
                              {row.totalUploaded?.toFixed(2)}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Inventory Update</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            The following {fixableItems.length} material(s) have inventory
            misallocated due to shared codes. The code-level totals match the
            uploaded data — only the per-material allocation will be corrected.
          </Typography>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>MFR</TableCell>
                  <TableCell>MPN</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Current Inventory</TableCell>
                  <TableCell align="right">New Inventory</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fixableItems.map((item) => (
                  <TableRow key={item.materialId}>
                    <TableCell>{item.mfr}</TableCell>
                    <TableCell>{item.mpn}</TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell align="right">{item.dbInventory}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "warning.main", fontWeight: "bold" }}
                    >
                      {item.updatedQuantity}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Only the <strong>inventory</strong> field will be updated. No
            records will be created or deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={applyFixes} variant="contained" color="warning">
            Apply Updates
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
